import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { capTask } from '@capacitor-community/sqlite';
import { OfflineDatabaseService } from '../offline/offline-database.service';
import { HttpsService } from './https.service';

interface ClienteLocalEnvelope {
  data: any[];
  cursor: string;
  serverUpdatedAt: string | null;
  savedAt: number;
  version: number;
}

interface ClienteCacheOptions {
  forceRefresh?: boolean;
  onCached?: (clientes: any[]) => void;
}

interface ClienteDetalheOptions {
  forceRefresh?: boolean;
  onCached?: (cliente: any) => void;
}

const CLIENTE_MYSQL_COLUMNS = [
  'id_cliente', 'user_email', 'user_id', 'nome_cliente', 'signature_pad',
  'nome_cliente_slug', 'cpf_cliente', 'cnpj_cliente', 'razao_social_cliente',
  'email_cliente', 'assinatura_cliente_url', 'assinatura_cliente_data',
  'assinatura_cliente_ip', 'telefone_cliente', 'celular_cliente', 'cep_cliente',
  'logradouro_cliente', 'number_cliente', 'complemento_cliente', 'bairro_cliente',
  'cidade_cliente', 'estado_cliente', 'desc_cliente', 'inscricao_estadual_cliente',
  'inscricao_municipal_cliente', 'pessoa_fisica', 'pessoa_juridica', 'create_date',
  'backup_status', 'status_cliente', 'origem_cliente', 'observacoes_cliente',
  'data_ultima_interacao'
] as const;

const CLIENTE_LOCAL_COLUMNS = [
  ...CLIENTE_MYSQL_COLUMNS,
  'possui_obra_vinculada', 'obras_liberadas', 'acesso_obras_ativo', 'local_updated_at'
] as const;

@Injectable({ providedIn: 'root' })
export class ClienteRepositoryService {
  private storage: Storage | null = null;
  private initialized = false;
  private storageInitialization: Promise<void> | null = null;
  private readonly version = 4;
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private readonly retryIntervalMs = 60 * 1000;
  private readonly syncByUser = new Map<number, Promise<any[]>>();
  private readonly lastCheckByUser = new Map<number, number>();
  private readonly nextRetryByUser = new Map<number, number>();
  private readonly cacheGenerationByUser = new Map<number, number>();

  constructor(
    private https: HttpsService,
    private database: OfflineDatabaseService,
  ) {}

  async initializeForUser(userId: any): Promise<any[]> {
    return this.synchronize(userId);
  }

  async getClientes(userId: any, options: ClienteCacheOptions = {}): Promise<any[]> {
    const cached = await this.readLocal(userId);
    if (cached.length) options.onCached?.(cached);

    try {
      return await this.synchronize(userId, !!options.forceRefresh);
    } catch (error) {
      if (cached.length || await this.hasLocalSnapshot(userId)) return cached;
      throw error;
    }
  }

  async refreshClientes(userId: any): Promise<any[]> {
    return this.synchronize(userId, true);
  }

  async getClienteDetalhe(userId: any, clienteId: any, options: ClienteDetalheOptions = {}): Promise<any | null> {
    const cached = (await this.readLocal(userId))
      .find((cliente) => this.getClienteId(cliente) == clienteId) || null;
    if (cached) options.onCached?.(cached);

    if (cached && !options.forceRefresh) return cached;

    try {
      const cliente = await this.fetchClienteDetalhe(clienteId);
      if (cliente) {
        await this.upsertCliente(userId, cliente);
        return cliente;
      }
    } catch (error) {
      if (!cached) throw error;
    }
    return cached;
  }

  async addClienteToCache(userId: any, cliente: any): Promise<void> {
    await this.upsertCliente(userId, cliente);
  }

  async updateClienteInCache(userId: any, cliente: any): Promise<void> {
    await this.upsertCliente(userId, cliente);
  }

  async upsertCliente(userId: any, cliente: any): Promise<void> {
    const id = this.getClienteId(cliente);
    if (!id) {
      await this.invalidateClientes(userId);
      return;
    }

    const clientes = await this.readLocal(userId);
    const existente = clientes.find((item) => this.getClienteId(item) == id);
    const next = existente
      ? clientes.map((item) => this.getClienteId(item) == id ? { ...item, ...cliente } : item)
      : [cliente, ...clientes];
    await this.replaceLocal(userId, this.sortClientes(next), await this.getLocalCursor(userId), null);
  }

  async removeClienteFromCache(userId: any, clienteId: any): Promise<void> {
    const clientes = await this.readLocal(userId);
    await this.replaceLocal(
      userId,
      clientes.filter((item) => this.getClienteId(item) != clienteId),
      await this.getLocalCursor(userId),
      null,
    );
  }

  async invalidateClientes(userId: any): Promise<void> {
    await this.initStorage();
    const normalizedUserId = this.normalizeUserId(userId);
    this.cacheGenerationByUser.set(normalizedUserId, (this.cacheGenerationByUser.get(normalizedUserId) || 0) + 1);
    this.lastCheckByUser.delete(normalizedUserId);
    this.nextRetryByUser.delete(normalizedUserId);
    await this.storage?.remove(this.cacheKey(normalizedUserId));
    await this.storage?.remove(`cache:user:${normalizedUserId}:clientes`);
    if (this.canUseNativeDatabase(normalizedUserId)) {
      await this.database.run('DELETE FROM clientes');
      await this.database.run('DELETE FROM sync_state WHERE scope = ?', ['clientes']);
    }
  }

  private synchronize(userId: any, force = false): Promise<any[]> {
    const normalizedUserId = this.normalizeUserId(userId);
    if (!normalizedUserId) return Promise.resolve([]);

    const current = this.syncByUser.get(normalizedUserId);
    if (current) return current;

    const now = Date.now();
    const nextRetry = this.nextRetryByUser.get(normalizedUserId) || 0;
    if (!force && now < nextRetry) {
      return this.readLocal(normalizedUserId);
    }

    const lastCheck = this.lastCheckByUser.get(normalizedUserId) || 0;
    if (!force && now - lastCheck < this.checkIntervalMs) {
      return this.readLocal(normalizedUserId);
    }

    const generation = this.cacheGenerationByUser.get(normalizedUserId) || 0;
    const operation = this.performSynchronization(normalizedUserId, force, generation)
      .then((clientes) => {
        this.nextRetryByUser.delete(normalizedUserId);
        return clientes;
      })
      .catch((error) => {
        this.nextRetryByUser.set(normalizedUserId, Date.now() + this.retryIntervalMs);
        throw error;
      })
      .finally(() => this.syncByUser.delete(normalizedUserId));
    this.syncByUser.set(normalizedUserId, operation);
    return operation;
  }

  private async performSynchronization(userId: number, force: boolean, generation: number): Promise<any[]> {
    await this.initStorage();
    if (Capacitor.isNativePlatform()
      && (!this.database.isReady || this.database.userId !== userId)) {
      await this.database.initializeForUser(userId);
    }
    const cursor = force ? '' : await this.getLocalCursor(userId);
    const formData = new FormData();
    formData.append('data', JSON.stringify({ callback: 'sync_clientes', cursor }));

    const response: any = await this.https.post(formData, 'cliente-sync.php');
    if (!response?.success) {
      throw new Error(response?.msg || 'Não foi possível sincronizar os clientes.');
    }
    if ((this.cacheGenerationByUser.get(userId) || 0) !== generation) return [];

    if (response.changed || force) {
      const clientes = Array.isArray(response.result) ? response.result : [];
      await this.replaceLocal(userId, clientes, String(response.cursor ?? '0'), response.updated_at || null);
    } else {
      await this.updateLocalCursor(userId, String(response.cursor ?? cursor ?? '0'), response.updated_at || null);
    }

    this.lastCheckByUser.set(userId, Date.now());
    return this.readLocal(userId);
  }

  private async readLocal(userId: any): Promise<any[]> {
    await this.initStorage();
    const normalizedUserId = this.normalizeUserId(userId);
    if (this.canUseNativeDatabase(normalizedUserId)) {
      const rows = await this.database.query<any>(
        'SELECT * FROM clientes ORDER BY nome_cliente COLLATE NOCASE ASC',
      );
      if (rows.length || await this.hasNativeSyncState()) {
        return rows.map((row) => this.normalizeCliente(row));
      }
      return this.sortClientes((await this.getLegacyEnvelope(normalizedUserId))?.data || []);
    }

    const envelope = await this.getWebEnvelope(normalizedUserId);
    return this.sortClientes(envelope?.data || []);
  }

  private async replaceLocal(userId: any, clientes: any[], cursor: string, serverUpdatedAt: string | null): Promise<void> {
    await this.initStorage();
    const normalizedUserId = this.normalizeUserId(userId);
    const normalized = this.sortClientes((clientes || []).map((cliente) => this.normalizeCliente(cliente)));

    if (this.canUseNativeDatabase(normalizedUserId)) {
      const now = new Date().toISOString();
      const placeholders = CLIENTE_LOCAL_COLUMNS.map(() => '?').join(', ');
      const tasks: capTask[] = [{ statement: 'DELETE FROM clientes' }];
      normalized.forEach((cliente) => tasks.push({
        statement: `INSERT INTO clientes (${CLIENTE_LOCAL_COLUMNS.join(', ')}) VALUES (${placeholders})`,
        values: CLIENTE_LOCAL_COLUMNS.map((column) => column === 'local_updated_at'
          ? now
          : this.sqliteValue(column, cliente[column])),
      }));
      tasks.push({
        statement: `INSERT INTO sync_state (scope, cursor, last_pulled_at, updated_at)
          VALUES ('clientes', ?, ?, ?)
          ON CONFLICT(scope) DO UPDATE SET cursor = excluded.cursor,
            last_pulled_at = excluded.last_pulled_at, updated_at = excluded.updated_at`,
        values: [cursor || '0', serverUpdatedAt || now, now],
      });
      await this.database.executeTransaction(tasks);
      return;
    }

    const envelope: ClienteLocalEnvelope = {
      data: normalized,
      cursor: cursor || '0',
      serverUpdatedAt,
      savedAt: Date.now(),
      version: this.version,
    };
    await this.storage?.set(this.cacheKey(normalizedUserId), envelope);
  }

  private async updateLocalCursor(userId: number, cursor: string, serverUpdatedAt: string | null): Promise<void> {
    if (this.canUseNativeDatabase(userId)) {
      const now = new Date().toISOString();
      await this.database.run(
        `INSERT INTO sync_state (scope, cursor, last_pulled_at, updated_at)
         VALUES ('clientes', ?, ?, ?)
         ON CONFLICT(scope) DO UPDATE SET cursor = excluded.cursor,
           last_pulled_at = excluded.last_pulled_at, updated_at = excluded.updated_at`,
        [cursor || '0', serverUpdatedAt || now, now],
      );
      return;
    }

    const envelope = await this.getWebEnvelope(userId);
    if (!envelope) return;
    await this.storage?.set(this.cacheKey(userId), {
      ...envelope,
      cursor: cursor || '0',
      serverUpdatedAt,
      savedAt: Date.now(),
    });
  }

  private async getLocalCursor(userId: number): Promise<string> {
    if (this.canUseNativeDatabase(userId)) {
      const rows = await this.database.query<{ cursor: string | null }>(
        'SELECT cursor FROM sync_state WHERE scope = ? LIMIT 1',
        ['clientes'],
      );
      return String(rows[0]?.cursor || '');
    }
    return String((await this.getWebEnvelope(userId))?.cursor || '');
  }

  private async hasLocalSnapshot(userId: any): Promise<boolean> {
    const normalizedUserId = this.normalizeUserId(userId);
    if (this.canUseNativeDatabase(normalizedUserId)) {
      const rows = await this.database.query<{ cursor: string | null }>(
        'SELECT cursor FROM sync_state WHERE scope = ? LIMIT 1',
        ['clientes'],
      );
      return rows.length > 0 || !!await this.getLegacyEnvelope(normalizedUserId);
    }
    return !!await this.getWebEnvelope(normalizedUserId);
  }

  private async fetchClienteDetalhe(clienteId: any): Promise<any | null> {
    const formData = new FormData();
    formData.append('data', JSON.stringify({ callback: 'read_cliente_detalhe', cliente_id: clienteId }));
    const response: any = await this.https.post(formData, 'cliente.php');
    return response?.success && Array.isArray(response.result) ? (response.result[0] || null) : null;
  }

  private async initStorage(): Promise<void> {
    if (this.initialized) return;
    if (!this.storageInitialization) {
      this.storageInitialization = this.createStorage();
    }
    await this.storageInitialization;
  }

  private async createStorage(): Promise<void> {
    const storage = new Storage({
      name: 'construfacil',
      storeName: 'app_cache',
      driverOrder: [Drivers.IndexedDB],
    });
    this.storage = await storage.create();
    this.initialized = true;
  }

  private async getWebEnvelope(userId: number): Promise<ClienteLocalEnvelope | null> {
    await this.initStorage();
    const cached = await this.storage?.get(this.cacheKey(userId));
    if (cached?.version === this.version && Array.isArray(cached.data)) {
      return cached as ClienteLocalEnvelope;
    }

    const legacy = await this.getLegacyEnvelope(userId);
    if (!legacy) return null;
    const migrated: ClienteLocalEnvelope = {
      data: legacy.data,
      cursor: '',
      serverUpdatedAt: null,
      savedAt: Number(legacy.savedAt || Date.now()),
      version: this.version,
    };
    await this.storage?.set(this.cacheKey(userId), migrated);
    return migrated;
  }

  private async getLegacyEnvelope(userId: number): Promise<{ data: any[]; savedAt?: number } | null> {
    await this.initStorage();
    const legacy = await this.storage?.get(`cache:user:${userId}:clientes`);
    return legacy && Array.isArray(legacy.data) ? legacy : null;
  }

  private async hasNativeSyncState(): Promise<boolean> {
    const rows = await this.database.query<{ scope: string }>(
      'SELECT scope FROM sync_state WHERE scope = ? LIMIT 1',
      ['clientes'],
    );
    return rows.length > 0;
  }

  private canUseNativeDatabase(userId: number): boolean {
    return Capacitor.isNativePlatform()
      && this.database.isReady
      && this.database.userId === userId;
  }

  private normalizeCliente(cliente: any): any {
    return {
      ...cliente,
      pessoa_fisica: Number(cliente?.pessoa_fisica || 0) === 1,
      pessoa_juridica: Number(cliente?.pessoa_juridica || 0) === 1,
      possui_obra_vinculada: Number(cliente?.possui_obra_vinculada || 0) === 1,
      obras_liberadas: Number(cliente?.obras_liberadas || 0),
      acesso_obras_ativo: Number(cliente?.acesso_obras_ativo || 0) === 1,
    };
  }

  private sqliteValue(column: string, value: any): any {
    if (['pessoa_fisica', 'pessoa_juridica', 'possui_obra_vinculada', 'acesso_obras_ativo'].includes(column)) {
      return value ? 1 : 0;
    }
    if (column === 'obras_liberadas' || column === 'backup_status' || column === 'user_id' || column === 'id_cliente') {
      return Number(value || 0);
    }
    return value ?? null;
  }

  private cacheKey(userId: number): string {
    return `offline:user:${userId}:clientes`;
  }

  private normalizeUserId(userId: any): number {
    return Math.max(0, Math.trunc(Number(userId || 0)));
  }

  private getClienteId(cliente: any): any {
    return cliente?.id_cliente || cliente?.cliente_id || null;
  }

  private sortClientes(clientes: any[]): any[] {
    return [...(clientes || [])].sort((a, b) =>
      String(a.nome_cliente || '').localeCompare(String(b.nome_cliente || ''), 'pt-BR'));
  }
}
